'use client';
import { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import { ArrowRight, X, ArrowUpRight } from "lucide-react";
import Image from 'next/image';
import { useTranslations, useLocale } from 'next-intl';
import LocaleChange from '@/app/lib/localeChange';
import { usePathname, useRouter } from 'next/navigation';
import ProjectService from '@/app/services/projects';
import { ProjectType } from '@/app/_types/projects';

export default function Header() {
  const t = useTranslations('Header');
  const router = useRouter();
  const pathname = usePathname();
  const isSpecialPath = pathname.charAt(3) === "";
  const isRealizationPath = pathname.includes("/services");

  const locale = useLocale();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [activeFilter, setActiveFilter] = useState<string | null>(null);
  const [data, setData] = useState<any[]>([]);
  const [error, setError] = useState<boolean>(false);

  const logoRef = useRef<HTMLDivElement>(null);
  const langNavRef = useRef<HTMLUListElement>(null);
  const mainNavRef = useRef<HTMLUListElement>(null);
  const menuNavRef = useRef<HTMLElement>(null);

  const onSelectLanguage = async (newLocale: string) => {
    if (locale !== newLocale) {
      document.cookie = `NEXT_LOCALE=${newLocale}; path=/; max-age=31536000; SameSite=Lax`;
      LocaleChange(newLocale);
    }
  };


  const updateSelectedType = (newType: string) => {
    localStorage.setItem("gfenetre_type", newType);
    window.dispatchEvent(new StorageEvent('storage', {
      key: 'gfenetre_type',
      newValue: newType,
    }));
  };

  async function getTypes() {
    try {
      const result: any = await ProjectService.getTypesList({ no_pagination: 1 });
      if (result?.data?.success) {
        setData(result?.data?.data);
        setActiveFilter(null)
        updateSelectedType('null')
        setError(false)
      }
    } catch (error: any) {
      setError(true)
    }
  }
  useEffect(() => {
    const handleStorageChange = (event: any) => {
      if (event.key === 'gfenetre_type') {
        setActiveFilter(event.newValue || 'null');
        updateSelectedType(event.newValue || 'null');
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  useEffect(() => {
    let cancelled = false;
    let ctx: any;
    import("gsap").then(({ gsap }) => {
      if (cancelled) return;
      ctx = gsap.context(() => {
        const tl = gsap.timeline({ defaults: { ease: "power3.out" } });
        tl
          .from(logoRef.current, { opacity: 0, x: -24, duration: 0.7 })
          .from(
            langNavRef.current ? Array.from(langNavRef.current.querySelectorAll('button')) : [],
            { opacity: 0, y: -10, duration: 0.45, stagger: 0.08 },
            "-=0.4"
          )
          .from(
            mainNavRef.current ? Array.from(mainNavRef.current.children) : [],
            { opacity: 0, y: -10, duration: 0.45, stagger: 0.08 },
            "-=0.35"
          );
      });
    });
    return () => { cancelled = true; ctx?.revert(); };
  }, []);

  useEffect(() => {
    if (!isOpen || !menuNavRef.current) return;
    import("gsap").then(({ gsap }) => {
      gsap.fromTo(
        Array.from(menuNavRef.current!.querySelectorAll('a')),
        { opacity: 0, x: -60 },
        { opacity: 1, x: 0, duration: 0.55, stagger: 0.09, ease: "power3.out", delay: 0.35 }
      );
    });
  }, [isOpen]);

  const homepage_class = "absolute inset-4 z-99 w-100 md:w-full mt-2 h-fit";
  const header_class = "bg-white border-b-1 border-slate-100 mt-2";

  useEffect(() => { getTypes() }, [])

  return (
    <>
      <header className={isSpecialPath ? homepage_class : header_class}>
        <div className="md:mx-auto px-4 sm:px-6 lg:px-8 md:pr-20 lg:pr-20">
          <div className="flex flex-col md:flex-row md:items-center justify-between h-16">

            <div ref={logoRef} className="flex-shrink-0">
              <Link href="/">
                <Image
                  src={isSpecialPath ? 'images/gss_white_logo.png' : '/images/gss_logo.png'}
                  alt="GSS logo"
                  className="h-auto md:justify-self-center mt-2"
                  width={120}
                  height={60}
                />
              </Link>
            </div>

            <nav className="md:flex flex-grow justify-center">
              <ul ref={langNavRef} className="flex justify-center md:justify-start space-x-2 md:space-x-8">
                <li>
                  {['fr', 'en', 'de'].map((lang) => (
                    <button
                      key={lang}
                      onClick={() => onSelectLanguage(lang)}
                      className={`${locale === lang
                          ? isSpecialPath ? "text-white" : "text-gray-600"
                          : "text-gray-400"
                        } cursor-pointer hover:text-gray-600 ps-1 md:px-2 py-2 rounded-md text-sm font-medium transition-colors duration-200`}
                    >
                      {lang.toUpperCase()}
                    </button>
                  ))}
                </li>
              </ul>
            </nav>

            <nav>
              <ul ref={mainNavRef} className="flex gap-2 md:gap-8 items-center justify-between md:justify-start border-t border-white/10 md:border-0 pt-0">
                {[
                  { name: "projects", href: "/projects" },
                  { name: "realizations", href: "/services" },
                  { name: "about", href: "/about" },
                  { name: "blog", href: "/blog" },
                ].map((item) => (
                  <li key={item.href}>
                    <Link
                      href={item.href}
                      onClick={() => setIsOpen(false)}
                      className="relative group flex justify-between w-full !no-underline pb-0.5"
                    >
                      <span className={`transition-colors text-md ${isSpecialPath ? "text-white" : "text-gray-600"} font-light`}>
                        {t(item.name)}
                      </span>
                      <span className={`absolute bottom-0 left-0 w-0 h-px ${isSpecialPath ? "bg-white" : "bg-gray-600"} group-hover:w-full transition-all duration-300`} />
                    </Link>
                  </li>
                ))}
                <li>
                  <Link
                    href="/contact-us"
                    onClick={() => setIsOpen(false)}
                    className="relative group flex justify-between w-full bg-primary px-3 py-1 !no-underline overflow-hidden"
                  >
                    <span className="group-hover:text-red-300 transition-colors text-md text-white font-light">
                      {t('contact')}
                    </span>
                  </Link>
                </li>
              </ul>
            </nav>

            <div className="flex hidden">
              <button
                id="menu-toggle"
                aria-label="Menu Toggle"
                type="button"
                onClick={() => setIsOpen(true)}
                className="inline-flex items-center cursor-pointer justify-center p-2 rounded-md text-gray-400 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
              >
                <svg className="block h-12 w-12" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 44 44" stroke="#000" aria-hidden="true">
                  <path strokeLinecap="square" strokeLinejoin="round" strokeWidth="2" d="M0 17h44M1 23h44M1 29h44" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </header>

      {!isSpecialPath && !isRealizationPath && (
        <nav className="mt-12 md:mt-0 border-b-1 border-slate-100 py-4 px-4 sm:px-6 lg:px-8 md:pr-20 lg:pr-20">
          <ul className="flex justify-center md:justify-end">
            {data?.map((type: ProjectType) => (
              <li key={type?.id}>
                <button onClick={() => { setActiveFilter(type.id); updateSelectedType(type?.id); router.push("/projects") }} className={`${activeFilter == type?.id ? "text-gray-900" : "text-gray-500"} cursor-pointer hover:text-gray-900 px-3 py-2 text-sm md:text-md font-medium`}>
                  {type[`name_${locale}` as keyof ProjectType]}
                </button>
              </li>
            ))}
          </ul>
        </nav>
      )}
    </>
  );
}
